// INTELIMOVE-EXT-014 — Contratos versionados de integração com CRM externo (primeiro conector previsto: CERES CRM).
// Neutros de provedor: o lado externo é o papel "CRM"; o catálogo de provedores diz qual produto ocupa esse papel.
// Nada aqui conhece tabela, tenant ou identificador interno do outro produto — só o que circula no fio.
// Documento de propriedade: docs/integrations/DATA_OWNERSHIP.md · contrato: docs/integrations/CONTRACTS.md.
import { z } from 'zod';

/** Nome e versão do contrato. Mudança incompatível = nova versão MAIOR; campos novos opcionais = versão menor. */
export const INTEGRATION_CONTRACT = 'intelimove.crm' as const;
export const INTEGRATION_CONTRACT_VERSION = '1.4' as const;
export const INTEGRATION_SPEC_VERSION = '1.0' as const;
/** Versões aceitas na entrada (a menor dentro da mesma MAIOR é compatível). */
export const ACCEPTED_CONTRACT_VERSIONS = ['1.0', '1.1', '1.2', '1.3', '1.4'] as const;
/** 1.1 (IM-EXT-015, aditiva): battlecard detalhado — campos opcionais; conexão em 1.0 recebe só o núcleo de 1.0. */
/** 1.2 (IM-EXT-017, aditiva): estimativa de mercado (TAM/SAM/SOM) — entidade e evento novos, nada muda no que já existia. */
/** 1.3 (IM-EXT-018-C01, aditiva): catálogo operacional do CRM (CATALOG_ITEM, CRM → InteliMove, sem preço) separado da
 *  oferta analítica (OFFERING, da InteliMove), que pode citar o item de catálogo vinculado (`catalogItemRef`). */
/** 1.4 (IM-EXT-022, aditiva): resultado comercial observado (COMMERCIAL_RESULT, CRM → InteliMove) — oportunidade com
 *  resultado, motivo, valor e datas, ou indicador agregado com a DEFINIÇÃO original. Alimenta benchmarks e aprendizado. */
export const CONTRACT_MINOR_FEATURES = { '1.1': ['BATTLECARD.detail'], '1.2': ['BATTLECARD.detail', 'MARKET_SIZING'], '1.3': ['BATTLECARD.detail', 'MARKET_SIZING', 'CATALOG_ITEM', 'OFFERING.catalogItemRef'], '1.4': ['BATTLECARD.detail', 'MARKET_SIZING', 'CATALOG_ITEM', 'OFFERING.catalogItemRef', 'COMMERCIAL_RESULT'] } as const;
/** Versão mínima do contrato por evento (eventos novos não valem numa conversa em versão anterior). */
export const EVENT_MIN_CONTRACT_VERSION: Partial<Record<string, string>> = { 'market_sizing.published': '1.2', 'catalog_item.upserted': '1.3', 'commercial_result.recorded': '1.4' };
const versionAtLeast = (v: string, min: string) => { const [a, b] = v.split('.').map(Number), [c, d] = min.split('.').map(Number); return a! > c! || (a === c && b! >= d!); };

/** Sistemas: a InteliMove e o CRM conectado (qualquer provedor que ocupe o papel de CRM). */
export const SYSTEMS = ['INTELIMOVE', 'CRM'] as const;
export type SystemRole = (typeof SYSTEMS)[number];

/** Assinatura HMAC-SHA256 de `${timestamp}.${corpo bruto}`, cabeçalho `v1=<hex>`; janela de 300 s contra replay. */
export const INTEGRATION_HEADERS = {
  eventId: 'x-event-id',
  timestamp: 'x-signature-timestamp',
  signature: 'x-signature',
  contractVersion: 'x-contract-version',
  idempotencyKey: 'idempotency-key',
  attempt: 'x-delivery-attempt',
} as const;
export const SIGNATURE_SCHEME = 'v1' as const;
export const SIGNATURE_TOLERANCE_SECONDS = 300;

// ---------------------------------------------------------------------------------------------------------------------
// Entidades
// ---------------------------------------------------------------------------------------------------------------------
export const INTEGRATION_ENTITIES = [
  'ORGANIZATION', 'TARGET_COMPANY', 'BUSINESS_IDENTITY', 'OFFERING', 'ICP', 'PERSONA', 'PERSON_CANDIDATE', 'SIGNAL',
  'RECOMMENDATION', 'COMPETITOR', 'BATTLECARD', 'DECISION', 'COMMERCIAL_OUTCOME', 'OBJECTION', 'WIN_LOSS',
  // 1.2 (IM-EXT-017): estimativa de mercado.
  'MARKET_SIZING',
  // 1.3 (IM-EXT-018-C01): item do catálogo operacional (dono: CRM).
  'CATALOG_ITEM',
  // 1.4 (IM-EXT-022): resultado comercial observado (dono: CRM).
  'COMMERCIAL_RESULT',
] as const;
export type IntegrationEntity = (typeof INTEGRATION_ENTITIES)[number];

const text = (max: number) => z.string().trim().min(1).max(max);
const url = z.string().url().max(2000);
const iso = z.string().datetime({ offset: true });
/** Referência a outra entidade do contrato: id local de quem é dono e, se já houver, o id do outro lado. */
export const EntityRef = z.object({ localId: text(200).optional(), externalId: text(200).optional() }).strict()
  .refine((r) => r.localId || r.externalId, 'referência sem identificador');
export type EntityRef = z.infer<typeof EntityRef>;

export const BusinessIdentityData = z.object({
  tradeName: text(200),
  legalName: text(300).optional(),
  taxId: z.string().regex(/^\d{14}$/).optional(),
  domain: z.string().trim().toLowerCase().max(253).regex(/^[a-z0-9.-]+\.[a-z]{2,}$/).optional(),
  website: url.optional(),
  country: z.string().length(2).default('BR'),
  uf: z.string().length(2).optional(),
  city: text(120).optional(),
  activityCode: z.string().regex(/^\d{4,7}(-?\d)?(\/?\d{2})?$/).optional(),
}).strict();

export const OrganizationData = z.object({
  name: text(200),
  website: url.optional(),
  taxId: z.string().regex(/^\d{14}$/).optional(),
  locale: z.string().max(10).default('pt-BR'),
}).strict();

const FIT_LEVELS = ['ALTA', 'MEDIA', 'BAIXA', 'INDETERMINADA'] as const;
export const TargetCompanyData = z.object({
  identity: BusinessIdentityData,
  status: z.enum(['CANDIDATE', 'REVIEW_REQUIRED', 'APPROVED', 'REJECTED', 'FOLLOWING', 'ARCHIVED']),
  segment: text(200).optional(),
  sizeBand: text(60).optional(),
  priority: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
  fit: z.object({
    level: z.enum(FIT_LEVELS),
    score: z.number().int().min(0).max(100).nullable().optional(),
    confidence: z.enum(['ALTA', 'MEDIA', 'BAIXA']).nullable().optional(),
    explanation: z.array(text(500)).max(30).default([]),
  }).strict(),
  offeringRefs: z.array(EntityRef).max(30).default([]),
  icpRef: EntityRef.optional(),
  evidenceUrls: z.array(url).max(50).default([]),
}).strict();

/**
 * OFERTA ANALÍTICA (dona: InteliMove): como a empresa descreve e analisa o que vende (contexto, ICP, segmentação,
 * concorrência, TAM/SAM/SOM, PESTEL, portfólio). Existe sem CRM. 1.3: pode citar o item do catálogo operacional vinculado.
 */
export const OfferingData = z.object({
  name: text(200), description: text(2000).optional(), category: text(120).optional(), status: z.enum(['ACTIVE', 'RETIRED']),
  catalogItemRef: EntityRef.optional(),
}).strict();

/** Campos comerciais que NUNCA circulam no catálogo (preço, margem, desconto, condição comercial, custo, estoque). */
export const CATALOG_FORBIDDEN_FIELDS = ['price', 'preco', 'prices', 'precos', 'margin', 'margem', 'discount', 'desconto', 'commercialConditions', 'condicoesComerciais', 'condicao', 'cost', 'custo', 'stock', 'estoque'] as const;
/**
 * ITEM DO CATÁLOGO OPERACIONAL (dono: CRM). Só os campos autorizados circulam; preço e condição comercial são recusados
 * na entrada. Na InteliMove vira fotografia SOMENTE LEITURA, versionada, que nunca sobrescreve a oferta analítica.
 */
export const CatalogItemData = z.object({
  code: text(80).optional(),
  name: text(200),
  kind: z.enum(['PRODUCT', 'SERVICE', 'PACKAGE', 'OTHER']),
  status: z.enum(['ACTIVE', 'INACTIVE', 'RETIRED']),
  description: text(2000).optional(),
  category: text(120).optional(),
  catalogVersion: text(40).optional(),
}).strict();
export type CatalogItemData = z.infer<typeof CatalogItemData>;

export const IcpData = z.object({
  name: text(200),
  description: text(2000).optional(),
  status: z.enum(['CONFIRMED', 'SUGGESTED', 'RETIRED']),
  criteria: z.array(z.object({ dimension: text(60), value: text(300), required: z.boolean() }).strict()).max(40).default([]),
}).strict();

export const PersonaData = z.object({
  name: text(200),
  roles: z.array(text(120)).max(20).default([]),
  seniority: text(60).optional(),
  pains: z.array(text(300)).max(20).default([]),
  goals: z.array(text(300)).max(20).default([]),
  icpRefs: z.array(EntityRef).max(20).default([]),
}).strict();

/** Pessoa candidata: só dado PÚBLICO e profissional; sem e-mail/telefone pessoal (LGPD: fica fora do contrato). */
export const PersonCandidateData = z.object({
  name: text(200),
  title: text(200).optional(),
  seniority: text(60).optional(),
  buyingRole: z.enum(['DECISOR', 'INFLUENCIADOR', 'USUARIO', 'TECNICO', 'DESCONHECIDO']).optional(),
  personaRef: EntityRef.optional(),
  companyRef: EntityRef,
  confirmed: z.boolean(),
  sourceUrls: z.array(url).min(1).max(20),
  lastVerifiedAt: iso.optional(),
}).strict();

export const SignalData = z.object({
  title: text(300),
  category: text(60),
  companyRef: EntityRef.optional(),
  timing: z.enum(['ABORDAR_AGORA', 'ACOMPANHAR_DE_PERTO', 'MONITORAR', 'SEM_URGENCIA']).optional(),
  commercialReason: text(1000).optional(),
  confidence: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
  detectedAt: iso,
  evidenceUrls: z.array(url).min(1).max(20),
}).strict();

export const RecommendationData = z.object({
  companyRef: EntityRef,
  approach: text(2000),
  rationale: text(2000).optional(),
  offeringRefs: z.array(EntityRef).max(20).default([]),
  timing: text(120).optional(),
  nature: z.literal('RECOMENDACAO'),
}).strict();

export const CompetitorData = z.object({
  identity: BusinessIdentityData,
  status: z.enum(['ACTIVE', 'ARCHIVED']),
  positioning: text(2000).optional(),
  strengths: z.array(text(300)).max(30).default([]),
  weaknesses: z.array(text(300)).max(30).default([]),
}).strict();

/** Item do battlecard que circula (1.1): só item CONFIRMADO por pessoa; classificação e fontes públicas, nunca o rascunho. */
export const BattlecardItemData = z.object({
  text: text(1000),
  classification: z.enum(['FATO', 'INDICIO', 'HIPOTESE', 'DECLARADO', 'NAO_ENCONTRADO']),
  confidence: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
  sourceUrls: z.array(url).max(10).default([]),
}).strict();
export const BattlecardData = z.object({
  competitorRef: EntityRef,
  title: text(200),
  howWeWin: z.array(text(500)).max(30).default([]),
  objectionHandling: z.array(z.object({ objection: text(500), response: text(1000) }).strict()).max(30).default([]),
  // 1.1 — campos opcionais (aditivos).
  version: z.number().int().min(1).optional(),
  reviewedAt: iso.optional(),
  positioningRole: z.enum(['DIRETO', 'INDIRETO', 'SUBSTITUTO']).optional(),
  summary: text(1500).optional(),
  whenTheyWin: z.array(text(500)).max(30).optional(),
  discoveryQuestions: z.array(text(500)).max(30).optional(),
  strengths: z.array(BattlecardItemData).max(30).optional(),
  weaknesses: z.array(BattlecardItemData).max(30).optional(),
  risks: z.array(BattlecardItemData).max(30).optional(),
  opportunities: z.array(BattlecardItemData).max(30).optional(),
  publicPrices: z.array(BattlecardItemData).max(30).optional(),
}).strict();

/** Estimativa de mercado (1.2): TAM/SAM/SOM em faixas, com as premissas ATIVAS e a fonte de cada número. */
const SizingRangeData = z.object({ low: z.number().min(0), likely: z.number().min(0), high: z.number().min(0) }).strict()
  .refine((r) => r.low <= r.likely && r.likely <= r.high, 'faixa fora de ordem');
export const MarketSizingData = z.object({
  title: text(200),
  version: z.number().int().min(1),
  calculatedAt: iso,
  method: z.enum(['TOP_DOWN', 'BOTTOM_UP', 'COMBINED']),
  currency: z.enum(['BRL', 'USD', 'EUR']),
  period: z.object({ label: text(60), start: z.string().regex(/^\d{4}-\d{2}$/).optional(), end: z.string().regex(/^\d{4}-\d{2}$/).optional() }).strict(),
  scope: z.object({
    offeringRefs: z.array(EntityRef).max(30).default([]),
    icpRefs: z.array(EntityRef).max(20).default([]),
    segments: z.array(text(120)).max(20).default([]),
    region: text(300),
    activityCodes: z.array(z.string().regex(/^\d{2,7}$/)).max(20).default([]),
  }).strict(),
  levels: z.array(z.object({
    level: z.enum(['TAM', 'SAM', 'SOM']),
    status: z.enum(['ESTIMADO', 'DADOS_INSUFICIENTES']),
    companies: SizingRangeData.optional(),
    value: SizingRangeData.optional(),
    confidence: z.enum(['ALTA', 'MEDIA', 'BAIXA']).optional(),
    missing: z.array(text(500)).max(10).default([]),
  }).strict()).length(3),
  assumptions: z.array(z.object({
    label: text(140), role: text(40), kind: z.enum(['COUNT', 'RATE', 'MONEY', 'FX']),
    low: z.number().min(0), likely: z.number().min(0), high: z.number().min(0),
    unit: z.enum(['EMPRESAS', 'ESTABELECIMENTOS', 'PESSOAS']).optional(),
    currency: z.enum(['BRL', 'USD', 'EUR']).optional(), fromCurrency: z.enum(['BRL', 'USD', 'EUR']).optional(), per: z.enum(['ANO', 'MES']).optional(),
    sourceKind: z.enum(['OFICIAL', 'PUBLICA', 'OBSERVADO', 'INTERNA', 'EQUIPE', 'IA']),
    sourceName: text(200), sourceUrl: url.optional(), referenceDate: z.string().max(10).optional(),
    confidence: z.enum(['ALTA', 'MEDIA', 'BAIXA']),
  }).strict()).max(40),
  limitations: z.array(text(500)).max(20).default([]),
}).strict();

export const DecisionData = z.object({
  subject: z.object({ type: z.enum(INTEGRATION_ENTITIES), ref: EntityRef }).strict(),
  decision: text(60),
  fromStatus: text(60).optional(),
  toStatus: text(60),
  reason: text(1000).optional(),
  automatic: z.boolean(),
  decidedAt: iso,
}).strict();

export const CommercialOutcomeData = z.object({
  accountRef: EntityRef,
  companyRef: EntityRef.optional(),
  stage: text(80),
  outcome: z.enum(['OPEN', 'WON', 'LOST', 'NO_DECISION']),
  occurredAt: iso,
}).strict();

export const ObjectionData = z.object({
  accountRef: EntityRef.optional(),
  companyRef: EntityRef.optional(),
  competitorRef: EntityRef.optional(),
  category: text(80),
  text: text(2000),
  occurredAt: iso,
}).strict();

export const WinLossData = z.object({
  opportunityRef: EntityRef,
  accountRef: EntityRef.optional(),
  result: z.enum(['WON', 'LOST']),
  reasonCategory: text(80),
  competitorRef: EntityRef.optional(),
  closedAt: iso,
}).strict();

/** 1.4 — resultado comercial observado: oportunidade (resultado, motivo, valor, datas) ou indicador agregado com a definição
 *  ORIGINAL da fonte. Referências: `localId` = id da InteliMove; `externalId` = id do CRM já vinculado. Sem contatos pessoais,
 *  propostas, condições comerciais nem anotações privadas. Vínculo que não casar fica "não associado" para revisão. */
const ResultRef = EntityRef;
export const CommercialResultData = z.object({
  resultType: z.enum(['OPPORTUNITY', 'INDICATOR']),
  opportunityRef: ResultRef.optional(), accountRef: ResultRef.optional(), companyRef: ResultRef.optional(),
  segment: text(160).optional(), icpRef: ResultRef.optional(), offeringRef: ResultRef.optional(), channel: text(80).optional(), origin: text(120).optional(), region: text(120).optional(),
  signalRef: ResultRef.optional(), competitorRef: ResultRef.optional(), decisionRef: ResultRef.optional(), scenarioRef: ResultRef.optional(),
  stage: text(80).optional(), outcome: z.enum(['OPEN', 'WON', 'LOST', 'NO_DECISION']).optional(),
  reasonCategory: z.enum(['PRECO', 'PRODUTO', 'CONCORRENTE', 'TIMING', 'ORCAMENTO', 'SEM_RESPOSTA', 'RELACIONAMENTO', 'OUTRO']).optional(), reasonText: text(500).optional(),
  amount: z.number().finite().min(0).optional(), currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  openedAt: iso.optional(), closedAt: iso.optional(), cycleDays: z.number().int().min(0).optional(), ownerRole: text(80).optional(),
  status: z.enum(['PARTIAL', 'FINAL', 'ESTIMATED', 'CONFIRMED']).optional(),
  indicator: z.object({ key: z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/), name: text(120).optional(), definition: text(600), value: z.number().finite(), unit: text(40), periodStart: iso, periodEnd: iso }).strict().optional(),
}).strict().superRefine((d, ctx) => {
  if (d.resultType === 'OPPORTUNITY' && !d.outcome) ctx.addIssue({ code: 'custom', path: ['outcome'], message: 'oportunidade sem resultado' });
  if (d.resultType === 'INDICATOR' && !d.indicator) ctx.addIssue({ code: 'custom', path: ['indicator'], message: 'indicador sem definição e valor' });
  if (d.amount !== undefined && !d.currency) ctx.addIssue({ code: 'custom', path: ['currency'], message: 'valor sem moeda' });
});

export const ENTITY_DATA_SCHEMAS: Record<IntegrationEntity, z.ZodType> = {
  ORGANIZATION: OrganizationData, TARGET_COMPANY: TargetCompanyData, BUSINESS_IDENTITY: BusinessIdentityData, OFFERING: OfferingData,
  ICP: IcpData, PERSONA: PersonaData, PERSON_CANDIDATE: PersonCandidateData, SIGNAL: SignalData, RECOMMENDATION: RecommendationData,
  COMPETITOR: CompetitorData, BATTLECARD: BattlecardData, DECISION: DecisionData, COMMERCIAL_OUTCOME: CommercialOutcomeData,
  OBJECTION: ObjectionData, WIN_LOSS: WinLossData, MARKET_SIZING: MarketSizingData, CATALOG_ITEM: CatalogItemData, COMMERCIAL_RESULT: CommercialResultData,
};

// ---------------------------------------------------------------------------------------------------------------------
// Propriedade dos dados (fonte de verdade executável do documento DATA_OWNERSHIP.md)
// ---------------------------------------------------------------------------------------------------------------------
export type SyncDirection = 'IM_TO_CRM' | 'CRM_TO_IM' | 'REFERENCE_ONLY';
export type ConflictPolicy = 'OWNER_WINS' | 'OWNER_WINS_HUMAN_LOCK' | 'HIGHER_VERSION_WINS' | 'APPEND_ONLY';
export interface OwnershipRule {
  owner: SystemRole;
  direction: SyncDirection;
  /** PREPARED = contrato pronto; OUT_OF_SCOPE = contrato publicado, processamento fora desta fase. */
  phase: 'PREPARED' | 'OUT_OF_SCOPE';
  shareable: readonly string[];
  neverShared: readonly string[];
  update: 'FULL_STATE' | 'APPEND' | 'MAPPING';
  conflict: ConflictPolicy;
}

const IM = (shareable: string[], neverShared: string[], conflict: ConflictPolicy = 'OWNER_WINS_HUMAN_LOCK', update: OwnershipRule['update'] = 'FULL_STATE'): OwnershipRule =>
  ({ owner: 'INTELIMOVE', direction: 'IM_TO_CRM', phase: 'PREPARED', shareable, neverShared, update, conflict });
const CRM = (shareable: string[], neverShared: string[], phase: OwnershipRule['phase'] = 'PREPARED', update: OwnershipRule['update'] = 'APPEND'): OwnershipRule =>
  ({ owner: 'CRM', direction: 'CRM_TO_IM', phase, shareable, neverShared, update, conflict: 'HIGHER_VERSION_WINS' });

export const DATA_OWNERSHIP: Record<IntegrationEntity, OwnershipRule> = {
  ORGANIZATION: { owner: 'INTELIMOVE', direction: 'REFERENCE_ONLY', phase: 'PREPARED', shareable: ['name', 'website', 'taxId', 'locale'], neverShared: ['usuários', 'papéis', 'sessões', 'assinatura/plano', 'segredos'], update: 'MAPPING', conflict: 'OWNER_WINS' },
  BUSINESS_IDENTITY: { owner: 'INTELIMOVE', direction: 'REFERENCE_ONLY', phase: 'PREPARED', shareable: ['tradeName', 'legalName', 'taxId', 'domain', 'website', 'country', 'uf', 'city', 'activityCode'], neverShared: ['rastro técnico da conferência', 'candidatos descartados'], update: 'MAPPING', conflict: 'OWNER_WINS_HUMAN_LOCK' },
  TARGET_COMPANY: IM(['identity', 'status', 'segment', 'sizeBand', 'priority', 'fit', 'offeringRefs', 'icpRef', 'evidenceUrls'], ['prompts e respostas de IA', 'consumo e créditos', 'pistas descartadas', 'notas internas de pesquisa']),
  // Oferta ANALÍTICA: da InteliMove; o catálogo operacional é outra entidade (CATALOG_ITEM, do CRM).
  OFFERING: IM(['name', 'description', 'category', 'status', 'catalogItemRef'], ['preço', 'margem', 'desconto', 'condição comercial', 'catálogo operacional (pertence ao CRM: CATALOG_ITEM)']),
  ICP: IM(['name', 'description', 'status', 'criteria'], ['sugestões não confirmadas sem marcação', 'pesos internos']),
  PERSONA: IM(['name', 'roles', 'seniority', 'pains', 'goals', 'icpRefs'], ['rascunhos não confirmados']),
  PERSON_CANDIDATE: IM(['name', 'title', 'seniority', 'buyingRole', 'personaRef', 'companyRef', 'confirmed', 'sourceUrls', 'lastVerifiedAt'], ['e-mail ou telefone pessoal', 'perfis privados', 'pessoas rejeitadas']),
  SIGNAL: IM(['title', 'category', 'companyRef', 'timing', 'commercialReason', 'confidence', 'detectedAt', 'evidenceUrls'], ['sinais demonstrativos ou técnicos', 'sinais sem evidência verificável', 'conteúdo integral de páginas']),
  RECOMMENDATION: IM(['companyRef', 'approach', 'rationale', 'offeringRefs', 'timing', 'nature'], ['raciocínio bruto de IA']),
  COMPETITOR: IM(['identity', 'status', 'positioning', 'strengths', 'weaknesses'], ['evidências brutas', 'análises em rascunho']),
  BATTLECARD: IM(['competitorRef', 'title', 'howWeWin', 'objectionHandling', 'version', 'reviewedAt', 'positioningRole', 'summary', 'whenTheyWin', 'discoveryQuestions', 'strengths', 'weaknesses', 'risks', 'opportunities', 'publicPrices'],
    ['rascunhos não revisados', 'itens pendentes ou rejeitados', 'observações internas da equipe', 'evidências brutas e trechos de páginas', 'versões anteriores']),
  MARKET_SIZING: IM(['title', 'version', 'calculatedAt', 'method', 'currency', 'period', 'scope', 'levels', 'assumptions', 'limitations'],
    ['premissas desativadas', 'justificativas internas da equipe', 'histórico de edições', 'versões anteriores', 'respostas brutas da fonte oficial'], 'HIGHER_VERSION_WINS'),
  DECISION: IM(['subject', 'decision', 'fromStatus', 'toStatus', 'reason', 'automatic', 'decidedAt'], ['identidade do usuário (só o papel USER/SYSTEM)'], 'APPEND_ONLY', 'APPEND'),
  COMMERCIAL_OUTCOME: CRM(['accountRef', 'companyRef', 'stage', 'outcome', 'occurredAt'], ['valores, propostas e negociação', 'contatos comerciais', 'atividades']),
  OBJECTION: CRM(['accountRef', 'companyRef', 'competitorRef', 'category', 'text', 'occurredAt'], ['autor da objeção', 'gravações e anotações privadas']),
  WIN_LOSS: CRM(['opportunityRef', 'accountRef', 'result', 'reasonCategory', 'competitorRef', 'closedAt'], ['valor do negócio', 'proposta', 'condições comerciais'], 'OUT_OF_SCOPE'),
  // Catálogo operacional: do CRM; na InteliMove é fotografia versionada somente leitura (nunca sobrescreve a oferta analítica).
  CATALOG_ITEM: CRM(['code', 'name', 'kind', 'status', 'description', 'category', 'catalogVersion'], ['preço', 'margem', 'desconto', 'condição comercial', 'custo', 'estoque'], 'PREPARED', 'FULL_STATE'),
  // Resultado comercial observado: do CRM; na InteliMove é espelho versionado para benchmarks e aprendizado (nunca editado pelo CRM depois de associado por pessoa).
  COMMERCIAL_RESULT: CRM(['resultType', 'opportunityRef', 'accountRef', 'companyRef', 'segment', 'icpRef', 'offeringRef', 'channel', 'origin', 'region', 'signalRef', 'competitorRef', 'decisionRef', 'scenarioRef', 'stage', 'outcome', 'reasonCategory', 'reasonText', 'amount', 'currency', 'openedAt', 'closedAt', 'cycleDays', 'ownerRole', 'status', 'indicator'],
    ['contatos pessoais', 'propostas', 'condições comerciais', 'anotações privadas', 'dados de outras organizações']),
};

/** Entidades exclusivas do CRM que NUNCA têm cópia de dono na InteliMove (a InteliMove só guarda referência). */
export const CRM_ONLY_CONCEPTS = ['conta operacional', 'contato comercial', 'lead', 'oportunidade', 'pipeline', 'atividades', 'cadências', 'propostas', 'negociações', 'catálogo e preços comerciais'] as const;

// ---------------------------------------------------------------------------------------------------------------------
// Eventos
// ---------------------------------------------------------------------------------------------------------------------
export interface EventSpec { entity: IntegrationEntity | null; direction: 'OUTBOUND' | 'INBOUND' | 'BOTH'; phase: 'PREPARED' | 'OUT_OF_SCOPE' }
export const INTEGRATION_EVENTS = {
  'integration.ping': { entity: null, direction: 'BOTH', phase: 'PREPARED' },
  'organization.linked': { entity: 'ORGANIZATION', direction: 'OUTBOUND', phase: 'PREPARED' },
  'business_identity.upserted': { entity: 'BUSINESS_IDENTITY', direction: 'OUTBOUND', phase: 'PREPARED' },
  'target_company.upserted': { entity: 'TARGET_COMPANY', direction: 'OUTBOUND', phase: 'PREPARED' },
  'offering.upserted': { entity: 'OFFERING', direction: 'OUTBOUND', phase: 'PREPARED' },
  'icp.upserted': { entity: 'ICP', direction: 'OUTBOUND', phase: 'PREPARED' },
  'persona.upserted': { entity: 'PERSONA', direction: 'OUTBOUND', phase: 'PREPARED' },
  'person_candidate.upserted': { entity: 'PERSON_CANDIDATE', direction: 'OUTBOUND', phase: 'PREPARED' },
  'signal.published': { entity: 'SIGNAL', direction: 'OUTBOUND', phase: 'PREPARED' },
  'recommendation.published': { entity: 'RECOMMENDATION', direction: 'OUTBOUND', phase: 'PREPARED' },
  'competitor.upserted': { entity: 'COMPETITOR', direction: 'OUTBOUND', phase: 'PREPARED' },
  'battlecard.upserted': { entity: 'BATTLECARD', direction: 'OUTBOUND', phase: 'PREPARED' },
  'market_sizing.published': { entity: 'MARKET_SIZING', direction: 'OUTBOUND', phase: 'PREPARED' },
  'decision.recorded': { entity: 'DECISION', direction: 'OUTBOUND', phase: 'PREPARED' },
  // Entrada (CRM → InteliMove).
  'account.linked': { entity: 'BUSINESS_IDENTITY', direction: 'INBOUND', phase: 'PREPARED' },
  'target_company.status_proposed': { entity: 'TARGET_COMPANY', direction: 'INBOUND', phase: 'PREPARED' },
  'commercial_outcome.recorded': { entity: 'COMMERCIAL_OUTCOME', direction: 'INBOUND', phase: 'PREPARED' },
  'objection.recorded': { entity: 'OBJECTION', direction: 'INBOUND', phase: 'PREPARED' },
  // 1.3: catálogo operacional (fotografia somente leitura; vínculo com a oferta analítica é decisão humana).
  'catalog_item.upserted': { entity: 'CATALOG_ITEM', direction: 'INBOUND', phase: 'PREPARED' },
  'commercial_result.recorded': { entity: 'COMMERCIAL_RESULT', direction: 'INBOUND', phase: 'PREPARED' },
  'win_loss.recorded': { entity: 'WIN_LOSS', direction: 'INBOUND', phase: 'OUT_OF_SCOPE' },
} as const satisfies Record<string, EventSpec>;
export type IntegrationEventType = keyof typeof INTEGRATION_EVENTS;
export const OUTBOUND_EVENTS = (Object.keys(INTEGRATION_EVENTS) as IntegrationEventType[]).filter((k) => INTEGRATION_EVENTS[k].direction !== 'INBOUND');
export const INBOUND_EVENTS = (Object.keys(INTEGRATION_EVENTS) as IntegrationEventType[]).filter((k) => INTEGRATION_EVENTS[k].direction !== 'OUTBOUND');

/** Dados de `account.linked`: o CRM informa que a entidade local X corresponde à conta Y dele. */
export const AccountLinkedData = z.object({ localRef: z.object({ type: z.enum(['TARGET_COMPANY', 'BUSINESS_IDENTITY', 'COMPETITOR']), localId: text(200) }).strict(), accountName: text(200).optional() }).strict();
/** Dados de `target_company.status_proposed`: o CRM SUGERE um estado para entidade da InteliMove (nunca aplica sozinho). */
export const StatusProposedData = z.object({ localId: text(200), proposedStatus: z.enum(['APPROVED', 'FOLLOWING', 'REJECTED']), reason: text(1000).optional() }).strict();
export const PingData = z.object({ message: text(200).optional() }).strict();

export const IntegrationEnvelope = z.object({
  specVersion: z.literal(INTEGRATION_SPEC_VERSION),
  contract: z.literal(INTEGRATION_CONTRACT),
  contractVersion: z.string().regex(/^\d+\.\d+$/),
  eventId: text(200).refine((v) => v.length >= 8, 'eventId curto demais'),
  eventType: z.enum(Object.keys(INTEGRATION_EVENTS) as [IntegrationEventType, ...IntegrationEventType[]]),
  occurredAt: iso,
  origin: z.object({
    system: z.enum(SYSTEMS),
    /** Organização de origem no sistema que emite (id externo, nunca o tenant interno do outro lado). */
    orgRef: text(200),
    actor: z.object({ type: z.enum(['USER', 'SYSTEM']), ref: text(200).optional() }).strict(),
  }).strict(),
  /** Evento que causou este (anti-loop): quem recebe não reemite mudança causada por evento do próprio par. */
  causationId: text(200).nullable().optional(),
  correlationId: text(200).nullable().optional(),
  idempotencyKey: text(200).refine((v) => v.length >= 8, 'chave curta demais'),
  entity: z.object({
    type: z.enum(INTEGRATION_ENTITIES),
    localId: text(200).optional(),
    externalId: text(200).optional(),
    /** Versão monotônica do dono; atualização com versão menor ou igual à já aplicada é ignorada (fora de ordem). */
    version: z.number().int().min(0),
    ownerSystem: z.enum(SYSTEMS),
  }).strict().nullable(),
  data: z.record(z.string(), z.unknown()),
}).strict();
export type IntegrationEnvelope = z.infer<typeof IntegrationEnvelope>;

/** Esquema dos dados de um evento (ping e eventos de entrada têm forma própria; os demais usam o da entidade). */
export function eventDataSchema(type: IntegrationEventType): z.ZodType {
  if (type === 'integration.ping') return PingData;
  if (type === 'account.linked') return AccountLinkedData;
  if (type === 'target_company.status_proposed') return StatusProposedData;
  const entity = INTEGRATION_EVENTS[type].entity;
  return entity ? ENTITY_DATA_SCHEMAS[entity] : PingData;
}

/** Valida envelope + dados. Devolve o envelope com `data` normalizado ou a lista de problemas. */
export function parseEnvelope(raw: unknown): { ok: true; envelope: IntegrationEnvelope } | { ok: false; issues: string[] } {
  const env = IntegrationEnvelope.safeParse(raw);
  if (!env.success) return { ok: false, issues: env.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`) };
  const major = env.data.contractVersion.split('.')[0];
  if (!ACCEPTED_CONTRACT_VERSIONS.some((v) => v.split('.')[0] === major)) return { ok: false, issues: [`contractVersion: versão ${env.data.contractVersion} não suportada`] };
  const min = EVENT_MIN_CONTRACT_VERSION[env.data.eventType];
  if (min && !versionAtLeast(env.data.contractVersion, min)) return { ok: false, issues: [`contractVersion: ${env.data.eventType} existe a partir da versão ${min} (recebida ${env.data.contractVersion})`] };
  if (env.data.eventType === 'catalog_item.upserted') {
    const bad = Object.keys(env.data.data).filter((k) => (CATALOG_FORBIDDEN_FIELDS as readonly string[]).includes(k));
    if (bad.length) return { ok: false, issues: bad.map((k) => `data.${k}: campo comercial não autorizado no catálogo (preço, margem, desconto e condição comercial não circulam)`) };
  }
  const spec = INTEGRATION_EVENTS[env.data.eventType];
  if (spec.entity && env.data.entity?.type !== spec.entity) return { ok: false, issues: [`entity.type: esperado ${spec.entity} para ${env.data.eventType}`] };
  const data = eventDataSchema(env.data.eventType).safeParse(env.data.data);
  if (!data.success) return { ok: false, issues: data.error.issues.map((i) => `data.${i.path.join('.')}: ${i.message}`) };
  return { ok: true, envelope: { ...env.data, data: data.data as Record<string, unknown> } };
}

// ---------------------------------------------------------------------------------------------------------------------
// API de integrações (IM-API-140..149)
// ---------------------------------------------------------------------------------------------------------------------
export const INTEGRATION_TRANSPORTS = ['SIMULATOR', 'HTTP'] as const;
export type IntegrationTransport = (typeof INTEGRATION_TRANSPORTS)[number];
export type IntegrationConnectionStatus = 'PENDING' | 'ACTIVE' | 'DEACTIVATED' | 'REVOKED';
/**
 * Estado mostrado ao usuário (sem detalhe técnico). IM-EXT-018-C01: o ambiente de validação (simulador) NUNCA aparece como
 * "conectado ao CRM" — tem estado próprio (VALIDATION); conexão real só vira CONNECTED com transporte HTTP ativo.
 */
export type IntegrationHealthState = 'NOT_CONNECTED' | 'VALIDATION' | 'PENDING_REAL' | 'CONNECTED' | 'PAUSED' | 'ATTENTION' | 'ERROR' | 'REVOKED';
export type IntegrationMode = 'SIMULATED' | 'REAL';

/** IM-API-141 corpo de "Conectar". */
export const CreateIntegrationConnectionRequest = z.object({
  transport: z.enum(INTEGRATION_TRANSPORTS),
  endpointUrl: z.string().trim().url().max(500).optional(),
  externalOrgId: z.string().trim().min(1).max(200).optional(),
  externalOrgName: z.string().trim().min(1).max(200).optional(),
  events: z.array(z.string().max(80)).max(40).optional(),
  /** Só para o simulador (homologação de falhas). */
  simulator: z.object({ failMode: z.enum(['NONE', 'TRANSIENT', 'PERMANENT']).default('NONE'), failTimes: z.number().int().min(0).max(20).default(0) }).strict().optional(),
}).strict();
export type CreateIntegrationConnectionRequest = z.infer<typeof CreateIntegrationConnectionRequest>;

export const RevokeIntegrationRequest = z.object({ reason: z.string().trim().max(500).optional() }).strict();

export interface IntegrationProviderDto {
  key: string;
  slug: string;
  name: string;
  description: string;
  role: 'CRM';
  contractVersion: string;
  transports: IntegrationTransport[];
  httpAvailable: boolean;
}

export interface IntegrationHealthDto {
  state: IntegrationHealthState;
  /** SIMULATED = ambiente de validação (nenhum dado vai ao CRM); REAL = transporte HTTP. Nulo sem conexão. */
  mode: IntegrationMode | null;
  /** Entregas reais ao CRM (`sent`) e entregas respondidas pelo simulador (`simulatedSent`) nunca se misturam. */
  simulatedSent: number;
  /** Frase curta para a tela principal (sem jargão). */
  summary: string;
  lastSyncAt: string | null;
  lastSuccessAt: string | null;
  sent: number;
  failed: number;
  pending: number;
  deadLetters: number;
  consecutiveFailures: number;
  lastError: string | null;
}

export interface IntegrationConnectionDto {
  id: string;
  provider: string;
  transport: IntegrationTransport;
  status: IntegrationConnectionStatus;
  contractVersion: string;
  endpointUrl: string | null;
  externalOrgId: string | null;
  externalOrgName: string | null;
  events: string[];
  secretHint: string | null;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  createdAt: string;
  activatedAt: string | null;
  deactivatedAt: string | null;
}

export interface IntegrationSummaryDto {
  provider: IntegrationProviderDto;
  connection: IntegrationConnectionDto | null;
  health: IntegrationHealthDto;
  canManage: boolean;
}

export interface IntegrationDeliveryDto {
  id: string;
  /** IM-EXT-018-C01: SIMULATOR (respondida pelo simulador; nada enviado) ou HTTP (envio real). */
  transport: IntegrationTransport;
  eventId: string;
  eventType: string;
  entityType: string | null;
  entityId: string | null;
  entityVersion: number;
  status: 'PENDING' | 'DELIVERING' | 'DELIVERED' | 'FAILED' | 'DEAD' | 'CANCELED' | 'SUPERSEDED';
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  lastStatusCode: number | null;
  deliveredAt: string | null;
  createdAt: string;
}

export interface IntegrationHistoryItemDto {
  at: string;
  transport?: IntegrationTransport | null;
  kind: 'AUDIT' | 'ATTEMPT' | 'INBOUND';
  action: string;
  detail: string | null;
  ok: boolean | null;
}

/** IM-API-141 resposta de "Conectar": segredos aparecem UMA vez (para configurar o outro lado) e nunca mais. */
export interface CreateIntegrationConnectionResponse {
  connection: IntegrationConnectionDto;
  secrets: { outbound: string; inbound: string; inboundUrl: string } | null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Catálogo operacional × oferta analítica (IM-EXT-018-C01)
// ---------------------------------------------------------------------------------------------------------------------
export interface CatalogItemDto {
  externalId: string; code: string | null; name: string; kind: CatalogItemData['kind']; status: CatalogItemData['status']; description: string | null; category: string | null;
  catalogVersion: string | null; version: number; receivedAt: string; simulated: boolean; sourceSystem: string; ownerSystem: 'CRM';
  link: {
    offeringId: string; offeringName: string; state: 'SYNCED' | 'CONFLICT'; acceptedVersion: number | null; conflictReason: string | null; linkedAt: string | null; linkedByName: string | null;
    /** Diferenças entre o que o catálogo diz AGORA e a oferta analítica confirmada (a oferta nunca é alterada sozinha). */
    diff: Array<{ field: 'name' | 'description'; offering: string | null; external: string | null }>;
  } | null;
}
export interface CatalogListDto {
  connection: { transport: IntegrationTransport; status: IntegrationConnectionStatus } | null;
  items: CatalogItemDto[];
  offerings: Array<{ id: string; name: string; origin: string; linkedExternalId: string | null }>;
}
export const SimulateCatalogItemRequest = z.object({
  externalId: z.string().trim().min(1).max(200), code: z.string().trim().max(80).optional(), name: z.string().trim().min(1).max(200),
  kind: z.enum(['PRODUCT', 'SERVICE', 'PACKAGE', 'OTHER']).default('SERVICE'), status: z.enum(['ACTIVE', 'INACTIVE', 'RETIRED']).default('ACTIVE'),
  description: z.string().trim().max(2000).optional(), category: z.string().trim().max(120).optional(),
}).strict();
export type SimulateCatalogItemRequest = z.infer<typeof SimulateCatalogItemRequest>;
export const LinkCatalogItemRequest = z.object({ offeringId: z.string().uuid() }).strict();
export const ResolveCatalogConflictRequest = z.object({
  offeringId: z.string().uuid(), decision: z.enum(['KEEP_CURATED', 'APPLY_EXTERNAL']), fields: z.array(z.enum(['name', 'description'])).max(2).optional(), justification: z.string().trim().min(3).max(1000),
}).strict();
export type ResolveCatalogConflictRequest = z.infer<typeof ResolveCatalogConflictRequest>;
